// Import necessary modules and components
import useFetch from "../../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../../components/page-header/page-header";
import useToast from "../../../../hooks/useToast";
import { TAssesment, TFilterModel } from "../../../../types/config";
import { eASSESSMENT_STATUS, eEVALUATION_CRITERIA, eHTTPStatusCode, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { defaultFilterParams, PageSize } from "../../../../utils/constants";
import {
  Button,
  Drawer,
  Dropdown,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
  Tag,
  Modal,
} from "antd";
import CustomDrawer from "../../../../components/drawer";
import { GetAssessment, DeleteKPI } from "../../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../../utils";
import { UserContext } from "../../../../context";
import {
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
  DeleteOutlined,
  CheckOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import KPIForm from "../kpis/form";
import PerformanceForm from "../kpis/performance";
import ViewPerformanceForm from "./viewperformance";
import AuthUtil from "../../../../utils/auth";
export default function GetAssessmentList() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TAssesment[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();

  const handleDelete = (record: TAssesment) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `You are about to delete the ${record.name}`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          setIsLoading(true);
          const payload = {
            data: {
              id: record.id,
            },
          };
          const response = await post(DeleteKPI, payload);
          if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
            onShowToast({
              type: ToastType.success,
              title: <CheckOutlined />,
              position: ToastOpen.centerTop,
              content: response.dataResponse.description,
            });
            fetchData();
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: response.dataResponse.description,
            });
          }
        } catch (error) {
          console.error("Error:", error);
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const initialColumns: any[] = [


    {
      title: "Title",
      dataIndex: "name",
      key: "name",
      width: "20%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: any) =>
        record.name?.includes(value as string),
    },
    {
      title: "Cycle",
      dataIndex: ["cycle", "name"],
      key: "cycle",
      width: "10%",
      filterSearch: true,
      filters: utils.getUniqueFilters(
        data.map((item: any) => ({
          cycleName: item.cycle?.name || "-"
        })),
        "cycleName"
      ),
      onFilter: (value: any, record: any) =>
        (record.cycle?.name || "").includes(value as string),
      sorter: (a: any, b: any) =>
        (a.cycle?.name || "").localeCompare(b.cycle?.name || ""),
    },
    {
      title: "Manager",
      dataIndex: ["user", "manager", "name"],
      key: "managerName",
      width: "15%",

      render: (_: any, record: any) =>
        record.user?.manager?.name || "-",

      sorter: (a: any, b: any) =>
        (a.user?.manager?.name || "").localeCompare(
          b.user?.manager?.name || ""
        ),
      filterSearch: true,
      filters: utils.getUniqueFilters(
        data.map((item: any) => ({
          managerName: item.user?.manager?.name || "-"
        })),
        "managerName"
      ),

      onFilter: (value: any, record: any) =>
        (record.user?.manager?.name || "") === (value as string),
    },
    {
      title: "Status",
      dataIndex: "action",
      key: "action",
      width: "20%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "action"),
      sorter: (a: any, b: any) => (a.action || "").localeCompare(b.action || ""),
      onFilter: (value: string | number | boolean, record: any) =>
        (record.action || "").includes(value as string),
    },
    {
      title: "Step",
      dataIndex: "statusName",
      key: "step",
      width: "20%",
      filterSearch: true,
      filters: Object.keys(eASSESSMENT_STATUS)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({ text: key.replace("_", " "), value: eASSESSMENT_STATUS[key as any] })),
      onFilter: (value: any, record: any) =>
        Number(record.statusName) === Number(value),
      render: (status: number) => {
        const label = eASSESSMENT_STATUS[status]?.replace("_", " ");

        const colorMap: any = {
          MANAGER_REVIEW: "blue",
          HR_REVIEW: "purple",
          EMPLOYEE_VERIFY: "orange",
          ACCEPTED: "green",
          REJECTED: "red",
          DISCUSSION: "gold",
          COMPLETED: "cyan",
          FINAL_ACCEPTANCE: "yellow",
        };

        return <Tag color={colorMap[eASSESSMENT_STATUS[status]]}>{label}</Tag>;
      }
    },

    {
      title: "Rating / Grade",
      key: "ratingGrade",
      width: "15%",
      sorter: (a: any, b: any) => Number(a.rating) - Number(b.rating),
      render: (record: any) => {

        const rating = record.rating || record.finalScore;
        const grade = record.finalGrade || record.gradeLabel;

        return rating || grade ? `${rating ?? ""}${grade ? ` (${grade})` : ""}` : "-";
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "6%",
      render: (item: TAssesment) => (
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            items: [
              //  Utils.isUserHasAccess(
              //   context.user?.privilegeList,
              //   ePrivileges.EDITDesignation
              // ) &&
              {
                key: "view",
                label: (
                  <CustomDrawer
                    // size="large"
                    width={3000}
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <EyeOutlined style={{ fontSize: 12 }} />
                        View
                      </div>
                    }
                    component={ViewPerformanceForm}
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title={`View KPI ${item?.user?.fullName}`}
                  />
                ),
              },
            ].filter(Boolean) as any,
          }}
        >
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const { user } = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1, // Fetch all data for client-side filtering
        },
      };

      const response = await post(GetAssessment, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      console.log("response", response);
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TAssesment, index: number) => ({
          ...item,
          key: (item.id ?? "").toString(),

          name: item?.user?.fullName,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(formattedData.map((item: any) => item.name)),
        ).map((name) => ({ text: name, value: name }));

        const designationFilters = Array.from(
          new Set(
            formattedData.flatMap(
              (item: any) => item.designations?.map((d: any) => d.name) || [],
            ),
          ),
        )
          .filter(Boolean)
          .map((name) => ({ text: name, value: name }));

        const managerFilters = Array.from(
          new Set(formattedData.map((item: any) => item.user?.manager?.name || "-")),
        ).map((name) => ({ text: name, value: name }));

        const cycleFilters = Array.from(
          new Set(formattedData.map((item: any) => item.cycle?.name || "-")),
        ).map((name) => ({ text: name, value: name }));

        const actionFilters = Array.from(
          new Set(formattedData.map((item: any) => item.action).filter(Boolean)),
        ).map((action) => ({ text: action, value: action }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "designations") {
              return { ...col, filters: designationFilters };
            }
            if (col.key === "managerName") {
              return { ...col, filters: managerFilters };
            }
            if (col.key === "cycle") {
              return { ...col, filters: cycleFilters };
            }
            if (col.key === "action") {
              return { ...col, filters: actionFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        const filterKey = col.key || (Array.isArray(col.dataIndex) ? col.dataIndex.join(",") : col.dataIndex);
        return {
          ...col,
          filteredValue: filters[filterKey as string] || null,
          sortOrder: (col.dataIndex === sorter.field || col.key === sorter.field) ? sorter.order : null,
        };
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item: any) => {
    const search = searchText.toLowerCase();
    const matchesName = item.name?.toLowerCase().includes(search);
    const matchesManager = item.user?.manager?.name?.toLowerCase().includes(search);
    const matchesCycle = item.cycle?.name?.toLowerCase().includes(search);
    const matchesStep = eASSESSMENT_STATUS[item.statusName]?.replace("_", " ").toLowerCase().includes(search);
    const matchesAction = item.action?.toLowerCase().includes(search);

    return matchesName || matchesManager || matchesCycle || matchesStep || matchesAction;
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  const downloadExcel = async () => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          "cycleId": 1
        },
      };

      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASEURL}/downloadPerformanceExcel`, {
        method: "POST",
        headers: {
          ...AuthUtil.getToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.status === eHTTPStatusCode.OK) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `performance_report.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "Report downloaded successfully.",
        });
      } else if (response.status === eHTTPStatusCode.NOT_FOUND) {
        onShowToast({
          type: ToastType.info,
          title: <ExclamationCircleOutlined />,
          position: ToastOpen.centerTop,
          content: "No data found for the selected criteria.",
        });
      } else {
        const res = await response.json();
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content:
            res?.dataResponse?.description ||
            "Failed to download report. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      onShowToast({
        type: ToastType.error,
        position: ToastOpen.centerTop,
        content: "An error occurred while downloading the report.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total KPIs`}
        size="large"
      >
        <Space>
          {/* {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDPROJECTCATEGORY
          ) ? ( */}
          <CustomDrawer
            size="default"
            trigger={
              <Button disabled={isLoading} type="primary">
                Add
              </Button>
            }
            component={KPIForm}
            componentProps={{
              id: 0, // Pass necessary props for ServicePartnerForm
              onRefreshList: () => {
                fetchData();
                setFilterParams(defaultFilterParams);
              },
            }}
            title="Add KPI"
          />
          {/* ) : null} */}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <Tooltip title="Performance review Download Excel">
            <DownloadOutlined
              onClick={downloadExcel}
              type="primary"
            />
          </Tooltip>
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
          {/* <FontAwesomeIcon
          icon={faFilter}
          className="listFilterIcons"
        ></FontAwesomeIcon> */}
        </Space>
      </PageHeader>
      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
